import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';

import {
  gamesQuery,
  getGamesSessions,
  scenarioLevelsQuery,
  scoresInsightsQuery
} from './api';

import { popupMessagesSummaryBreakdown } from './data';

import PopupMessages from '../UI/introGameLayout/popupMessages';

import './styles.css';
import Analysis from '../UI/game/analysis';

export default function ThePrinciplesSummaryBreakdown() {
  const navigate = useNavigate();
  
  const { 
    getUser,
    getCurrentThePrinciplesGameId
  } = useAuth();

  const user = getUser();
  
  const gameId = getCurrentThePrinciplesGameId();

  const [currentGameSessionId, setCurrentGameSessionId] = useState(0);

  const [scenarioId, setScenarioId] = useState(0);

  const [data, setData] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const { data: gamesSessions } = useQuery({
    queryKey: ['gamesSessionsSummaryBreakdown'],
    queryFn: getGamesSessions,
    refetchOnWindowFocus: false,
  });

  const { data: games } = useQuery(gamesQuery()) || {};
  const { data: scenarioLevels } = useQuery(scenarioLevelsQuery({scenarioId}));
  const { data: scoresInsights } = useQuery(scoresInsightsQuery({gameId}));

  useEffect(() => {
    if(gamesSessions && gameId && user) {
      const currentGameSessions = gamesSessions.filter(session => session.user === user?.id && session.game === gameId);
      const currentGameSession = currentGameSessions.find(session => !session.completed);
      
      if (currentGameSession) {
        setCurrentGameSessionId(currentGameSession?.id);
      } else {
        const completedGameSession = gamesSessions
          .filter(session => session.user === user?.id && session.game === gameId && session.completed)
          .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);
  
        setCurrentGameSessionId(completedGameSession?.id);
      }
    }
  }, [gamesSessions, gameId, user]);

  useEffect(() => {
    if(games) {
      const game = games.find(game => game.game_type.includes('The Principles') && game.id === gameId);

      if(game && game.scenario) {
        setScenarioId(game.scenario);
      }
    }
  }, [games, gameId]);
  
  useEffect(() => {
    if(scoresInsights && scenarioLevels?.length && currentGameSessionId) {
      setDataLoaded(true);

      const currentSessionScoreInsights = scoresInsights.filter(insight => insight.game_session === currentGameSessionId);

      if(currentSessionScoreInsights.length > 0) {
        const revenueRecognitionItems = currentSessionScoreInsights.filter(item => item.principle_name === "Revenue Recognition");
        const revenueRecognitionScore = revenueRecognitionItems.length > 0 
          ? revenueRecognitionItems.reduce((sum, item) => sum + item.total_accuracy, 0) / revenueRecognitionItems.length
          : 0;

        const expenseRecognitionItems = currentSessionScoreInsights.filter(item => item.principle_name === "Expense Recognition");
        const expenseRecognitionScore = expenseRecognitionItems.length > 0
          ? expenseRecognitionItems.reduce((sum, item) => sum + item.total_accuracy, 0) / expenseRecognitionItems.length
          : 0;

        const otherItems = currentSessionScoreInsights.filter(
          item => item.principle_name !== "Revenue Recognition" && 
                  item.principle_name !== "Expense Recognition"
        );
        const othersScores = otherItems.length > 0
          ? otherItems.reduce((sum, item) => sum + item.total_accuracy, 0) / otherItems.length
          : 0;

        const sectionData = [
          ...(revenueRecognitionScore ? [{ title: "Revenue Recognition", score: revenueRecognitionScore * 100, color: '#B4E6EA' }] : []),
          ...(expenseRecognitionScore ? [{ title: "Expense Recognition", score: expenseRecognitionScore * 100, color: '#B4E6EA' }] : []),
          ...(othersScores ? [{ title: "Financing Activities & Other", score: othersScores * 100, color: '#B4E6EA' }] : []),
        ];
        
        const data = [
          ...(sectionData.length > 0 ? [{
            bars: sectionData,
            barsLabel: '%'
          }] : []),
        ];
        setData(data);
      }
    }
  }, [scoresInsights, scenarioLevels, currentGameSessionId]);
  
  const handlePopupMessagesNextButtonClick = () => {
    navigate('/the-principles/summary/decision-breakdown');
  }
  
  const handlePopupMessagesPrevButtonClick = () => {
    navigate('/the-principles/summary');
  }

  const maxScore = Math.max(...data.flatMap(section => 
    section.bars?.map(bar => bar.score) || []
  ));
  
  return(
    <Layout
      backgroundColor="#FBEDD5"
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      {
        dataLoaded &&
          <div className="game-summary-container">
            <PopupMessages
              messages={popupMessagesSummaryBreakdown}
              messageSize="small"
              buttonText="Next"
              onButtonClick={handlePopupMessagesNextButtonClick}
              additionalButtonText="Back"
              onAdditionalButtonClick={handlePopupMessagesPrevButtonClick}
            />
            <Analysis
              heading="Accuracy Breakdown"
              data={data}
              size="small"
              maxScore={maxScore}
              isBigScores={true}
              alignBarTitle="center"
            />
          </div>
      }
    </Layout>
  );
}
